import React, { useState, useRef } from 'react';
import { View, ScrollView, TextInput, StyleSheet, TouchableOpacity, Alert, Image, ActivityIndicator } from 'react-native';
import { Text } from '@app/design-system';
import { useThemeColors, useThemeAccent, storageService } from '@app/core';
import { useWebsiteBuilder } from '../../store/websiteBuilderStore';
import { validateAndSanitizeHtml, quickValidate } from '../../utils/htmlValidator';

// Gallery Image Preview Component
const GalleryImagePreview: React.FC<{ blobPath: string; alt: string }> = ({ blobPath, alt }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  React.useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);

        // Check if it's already a full URL
        if (blobPath.startsWith('http')) {
          setImageUrl(blobPath);
        } else {
          // Get read URL from storage service
          const readUrl = await storageService.getReadUrl(blobPath);
          setImageUrl(readUrl);
        }
      } catch (err) {
        console.error('❌ [GalleryPreview] Failed to load image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [blobPath]);

  if (loading) {
    return (
      <View style={{
        width: '100%',
        height: 150,
        backgroundColor: '#F5F5F7',
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 8
      }}>
        <ActivityIndicator size="small" color="#007AFF" />
        <Text style={{ marginTop: 8, fontSize: 12, color: '#86868B' }}>Loading image...</Text>
      </View>
    );
  }

  if (error || !imageUrl) {
    return (
      <View style={{
        width: '100%',
        height: 150,
        backgroundColor: '#F5F5F7',
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        marginVertical: 8
      }}>
        <Text style={{ fontSize: 32 }}>🖼️</Text>
        <Text style={{ marginTop: 8, fontSize: 12, color: '#FF3B30' }}>Failed to load image</Text>
      </View>
    );
  }

  return (
    <View style={{ marginVertical: 8 }}>
      <Image
        source={{ uri: imageUrl }}
        style={{
          width: '100%',
          height: 150,
          borderRadius: 8,
          backgroundColor: '#F5F5F7'
        }}
        resizeMode="cover"
        alt={alt}
      />
    </View>
  );
};

export const RightPanel: React.FC = () => {
  const themeColors = useThemeColors() || { primary: '#34C759', secondary: '#F5F5F5', text: '#000000', background: '#FFFFFF' };
  const accentColor = useThemeAccent() || '#FF9500';
  const { config, selectedBlockId, updateBlock, removeBlock, updateTheme } = useWebsiteBuilder();
  const [activeTab, setActiveTab] = useState<'settings' | 'style' | 'content'>('settings');
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);

  // Image upload state for HERO_BANNER
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image Gallery upload state
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState(0);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState<{ [key: string]: string }>({});
  const galleryFileInputRef = useRef<HTMLInputElement>(null);

  // Custom HTML state
  const [customHtml, setCustomHtml] = useState('');
  const [htmlErrors, setHtmlErrors] = useState<string[]>([]);
  const [htmlWarnings, setHtmlWarnings] = useState<string[]>([]);
  const [isValidatingHtml, setIsValidatingHtml] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);

  const selectedBlock = config?.blocks?.find(b => b.id === selectedBlockId);

  // Sync customHtml state when CUSTOM_HTML block is selected or changed
  React.useEffect(() => {
    if (selectedBlock?.type === 'CUSTOM_HTML') {
      setCustomHtml(selectedBlock.config.html || '');
      setHtmlErrors([]);
      setHtmlWarnings([]);
    }
  }, [selectedBlock?.id]);

  // Load image preview when block is selected (MUST be before any conditional returns)
  React.useEffect(() => {
    const loadImagePreview = async () => {
      // HERO_BANNER background image
      if (selectedBlock?.type === 'HERO_BANNER' && selectedBlock.config.bgImage) {
        const blobPath = selectedBlock.config.bgImage;
        // Check if it's already a full URL or a blob path
        if (blobPath.startsWith('http')) {
          setImagePreviewUrl(blobPath);
        } else {
          try {
            const readUrl = await storageService.getReadUrl(blobPath);
            setImagePreviewUrl(readUrl);
          } catch (error) {
            console.error('❌ [RightPanel] Failed to load image preview:', error);
            setImagePreviewUrl(null);
          }
        }
      }
      // HEADER logo image
      else if (selectedBlock?.type === 'HEADER' && selectedBlock.config.logo) {
        const blobPath = selectedBlock.config.logo;
        // Check if it's already a full URL or a blob path
        if (blobPath.startsWith('http')) {
          setImagePreviewUrl(blobPath);
        } else {
          try {
            const readUrl = await storageService.getReadUrl(blobPath);
            setImagePreviewUrl(readUrl);
          } catch (error) {
            console.error('❌ [RightPanel] Failed to load logo preview:', error);
            setImagePreviewUrl(null);
          }
        }
      }
      // IMAGE_GALLERY images
      else if (selectedBlock?.type === 'IMAGE_GALLERY' && selectedBlock.config.images) {
        const images = selectedBlock.config.images || [];
        const previews: { [key: string]: string } = {};

        for (const image of images) {
          const blobPath = image.blobPath;
          if (!blobPath) continue;

          try {
            if (blobPath.startsWith('http')) {
              previews[image.id] = blobPath;
            } else {
              const readUrl = await storageService.getReadUrl(blobPath);
              previews[image.id] = readUrl;
            }
          } catch (error) {
            console.error('❌ [RightPanel] Failed to load gallery image preview:', image.id, error);
          }
        }
        setGalleryImagePreviews(previews);
        setImagePreviewUrl(null);
      } else {
        setImagePreviewUrl(null);
        setGalleryImagePreviews({});
      }
    };

    loadImagePreview();
  }, [selectedBlock?.id, selectedBlock?.config.bgImage, selectedBlock?.config.logo, selectedBlock?.config.images]);

  // Function definitions (must be before conditional return)

  // Helper function to get platform colors
  const getPlatformColor = (platform: string): string => {
    const colors: Record<string, string> = {
      'Instagram': '#E4405F',
      'Twitter': '#1DA1F2',
      'X': '#000000',
      'Threads': '#000000',
      'YouTube': '#FF0000',
      'Facebook': '#1877F2',
      'LinkedIn': '#0A66C2',
      'TikTok': '#000000',
      'Pinterest': '#E60023',
      'WhatsApp': '#25D366'
    };
    return colors[platform] || '#666666';
  };

  // Helper function to get platform icons
  const getPlatformIcon = (platform: string): string => {
    const icons: Record<string, string> = {
      'Instagram': '📷',
      'Twitter': '🐦',
      'X': '✖️',
      'Threads': '🧵',
      'YouTube': '▶️',
      'Facebook': '👍',
      'LinkedIn': '💼',
      'TikTok': '🎵',
      'Pinterest': '📌',
      'WhatsApp': '💬'
    };
    return icons[platform] || '📱';
  };

  const handleConfigChange = (key: string, value: any) => {
    if (!selectedBlock) return;
    console.log('🔧 [RightPanel] Updating block config:', key, '=', value);
    console.log('🔧 [RightPanel] Current block config before update:', selectedBlock.config);
    updateBlock(selectedBlock.id, {
      config: {
        ...selectedBlock.config,
        [key]: value,
      },
    });
    console.log('🔧 [RightPanel] Update block called with:', {
      blockId: selectedBlock.id,
      config: {
        ...selectedBlock.config,
        [key]: value,
      }
    });
  };

  const handleRemoveBlock = () => {
    if (!selectedBlock) return;
    if (confirm('Are you sure you want to remove this block?')) {
      removeBlock(selectedBlock.id);
    }
  };

  // Handle image upload for HERO_BANNER background
  const handleBrowseImage = () => {
    console.log('📁 [RightPanel] Browse image clicked');
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('❌ [RightPanel] No file selected');
      return;
    }

    console.log('📦 [RightPanel] File selected:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Validate file
    const validation = storageService.validateFile(file, 5);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    try {
      setUploadingImage(true);
      setUploadProgress(0);

      // Get shopId from config or use template identifier
      const shopId = config?.shopId || 'template';

      console.log('📤 [RightPanel] Starting upload for shopId:', shopId);

      // Upload to Azure Blob and get blob path
      const blobPath = await storageService.uploadImage(
        file,
        shopId,
        'website',
        selectedBlock?.id || 'temp',
        (progress) => {
          console.log('📊 [RightPanel] Upload progress:', progress.percentage + '%');
          setUploadProgress(progress.percentage);
        }
      );

      console.log('✅ [RightPanel] Upload successful, blobPath:', blobPath);

      // Update block config with blob path (bgImage for HERO_BANNER, logo for HEADER)
      const configKey = selectedBlock?.type === 'HEADER' ? 'logo' : 'bgImage';
      handleConfigChange(configKey, blobPath);

      // Load preview using read URL
      const readUrl = await storageService.getReadUrl(blobPath);
      setImagePreviewUrl(readUrl);

      console.log('✅ [RightPanel] Image preview loaded');

    } catch (error) {
      console.error('❌ [RightPanel] Upload failed:', error);
      alert('Failed to upload image: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploadingImage(false);
      setUploadProgress(0);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle gallery image upload
  const handleBrowseGalleryImage = () => {
    console.log('📁 [RightPanel] Browse gallery image clicked');
    if (galleryFileInputRef.current) {
      galleryFileInputRef.current.click();
    }
  };

  const handleGalleryImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('❌ [RightPanel] No file selected');
      return;
    }

    // Check if we've reached the maximum limit (20 images)
    const currentImages = selectedBlock?.config.images || [];
    if (currentImages.length >= 20) {
      alert('Maximum 20 images allowed in gallery');
      return;
    }

    console.log('📦 [RightPanel] Gallery file selected:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    // Validate file
    const validation = storageService.validateFile(file, 5);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    try {
      setUploadingGalleryImage(true);
      setGalleryUploadProgress(0);

      // Get shopId from config or use template identifier
      const shopId = config?.shopId || 'template';

      console.log('📤 [RightPanel] Starting gallery upload for shopId:', shopId);

      // Upload to Azure Blob and get blob path
      const blobPath = await storageService.uploadImage(
        file,
        shopId,
        'website/gallery',
        selectedBlock?.id || 'temp',
        (progress) => {
          console.log('📊 [RightPanel] Gallery upload progress:', progress.percentage + '%');
          setGalleryUploadProgress(progress.percentage);
        }
      );

      console.log('✅ [RightPanel] Gallery upload successful, blobPath:', blobPath);

      // Create new image object
      const newImage = {
        id: `gallery-img-${Date.now()}`,
        imagePath: blobPath,
        url: blobPath, // For backward compatibility
        alt: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for alt text
        caption: '',
        order: currentImages.length
      };

      // Add to gallery images
      const updatedImages = [...currentImages, newImage];
      handleConfigChange('images', updatedImages);

      // Load preview using read URL
      const readUrl = await storageService.getReadUrl(blobPath);
      setGalleryImagePreviews(prev => ({
        ...prev,
        [newImage.id]: readUrl
      }));

      console.log('✅ [RightPanel] Gallery image added and preview loaded');

    } catch (error) {
      console.error('❌ [RightPanel] Gallery upload failed:', error);
      alert('Failed to upload gallery image: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setUploadingGalleryImage(false);
      setGalleryUploadProgress(0);
      // Clear file input
      if (galleryFileInputRef.current) {
        galleryFileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveGalleryImage = (imageId: string) => {
    if (!selectedBlock) return;

    const currentImages = selectedBlock.config.images || [];
    const updatedImages = currentImages.filter((img: any) => img.id !== imageId);

    // Re-order remaining images
    const reorderedImages = updatedImages.map((img: any, index: number) => ({
      ...img,
      order: index
    }));

    handleConfigChange('images', reorderedImages);

    // Remove from previews
    setGalleryImagePreviews(prev => {
      const updated = { ...prev };
      delete updated[imageId];
      return updated;
    });
  };

  const handleUpdateGalleryImage = (imageId: string, field: string, value: any) => {
    if (!selectedBlock) return;

    const currentImages = selectedBlock.config.images || [];
    const updatedImages = currentImages.map((img: any) =>
      img.id === imageId ? { ...img, [field]: value } : img
    );

    handleConfigChange('images', updatedImages);
  };

  if (!selectedBlock) {
    return (
      <View style={styles(themeColors, accentColor).container}>
        <View style={styles(themeColors, accentColor).emptyState}>
          <Text style={styles(themeColors, accentColor).emptyIcon}>👈</Text>
          <Text style={styles(themeColors, accentColor).emptyTitle}>No Block Selected</Text>
          <Text style={styles(themeColors, accentColor).emptyText}>
            Select a block from the canvas to edit its properties
          </Text>
        </View>
      </View>
    );
  }


  const tabs = [
    { id: 'settings', label: '⚙️ Settings', icon: '⚙️' },
    { id: 'style', label: '🎨 Style', icon: '🎨' },
    { id: 'content', label: '📝 Content', icon: '📝' },
  ];

  const renderSettings = () => {
    return (
      <View style={styles(themeColors, accentColor).section}>
        <Text style={styles(themeColors, accentColor).sectionTitle}>Block Settings</Text>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Block Type</Text>
          <Text style={styles(themeColors, accentColor).value}>{selectedBlock.type}</Text>
        </View>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Required Plan</Text>
          <Text style={styles(themeColors, accentColor).value}>{selectedBlock.requiredPlan}</Text>
        </View>

        <TouchableOpacity style={styles(themeColors, accentColor).deleteButton} onPress={handleRemoveBlock}>
          <Text style={styles(themeColors, accentColor).deleteButtonText}>🗑️ Remove Block</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderStyle = () => {

    // Predefined color palettes for quick selection
    const colorPalettes = [
      { name: 'Fresh Green', primary: '#34C759', secondary: '#F5F5F5', accent: '#FF9500' },
      { name: 'Ocean Blue', primary: '#007AFF', secondary: '#E8F4FD', accent: '#5AC8FA' },
      { name: 'Royal Purple', primary: '#AF52DE', secondary: '#F3E8FF', accent: '#FF2D55' },
      { name: 'Sunset Orange', primary: '#FF9500', secondary: '#FFF4E5', accent: '#FF3B30' },
      { name: 'Professional Gray', primary: '#8E8E93', secondary: '#F2F2F7', accent: '#007AFF' },
      { name: 'Nature Earth', primary: '#8B4513', secondary: '#FFF8DC', accent: '#228B22' },
    ];

    // Font family options
    const fontFamilies = [
      'Poppins, sans-serif',
      'Roboto, sans-serif',
      'Open Sans, sans-serif',
      'Lato, sans-serif',
      'Montserrat, sans-serif',
      'Raleway, sans-serif',
      'Inter, sans-serif',
      'Playfair Display, serif',
      'Merriweather, serif',
    ];

    // Layout modes
    const layoutModes = ['modern', 'classic', 'minimal', 'bold'];

    // Button styles
    const buttonStyles = ['rounded', 'square', 'pill', 'outline'];

    // Border radius options
    const borderRadiusOptions = ['4px', '8px', '12px', '16px', '24px'];

    const handleThemeChange = (key: string, value: any) => {
      if (!config) return;
      updateTheme({
        ...config.theme,
        [key]: value,
      });
    };

    const handleTypographyChange = (key: string, value: any) => {
      if (!config) return;
      updateTheme({
        ...config.theme,
        typography: {
          ...config.theme.typography,
          [key]: value,
        },
      });
    };

    const handleSpacingChange = (key: string, value: any) => {
      if (!config) return;
      updateTheme({
        ...config.theme,
        spacing: {
          ...config.theme.spacing,
          [key]: value,
        },
      });
    };

    const applyColorPalette = (palette: typeof colorPalettes[0]) => {
      if (!config) return;
      updateTheme({
        ...config.theme,
        primaryColor: palette.primary,
        secondaryColor: palette.secondary,
        accentColor: palette.accent,
      });
    };

    return (
      <View style={styles(themeColors, accentColor).section}>
        <Text style={styles(themeColors, accentColor).sectionTitle}>🎨 Global Theme Styles</Text>

        {/* Quick Color Palettes */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Quick Color Palettes</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
            {colorPalettes.map((palette, index) => (
              <TouchableOpacity
                key={index}
                style={styles(themeColors, accentColor).paletteCard}
                onPress={() => applyColorPalette(palette)}
              >
                <View style={styles(themeColors, accentColor).paletteColors}>
                  <View style={[styles(themeColors, accentColor).paletteColor, { backgroundColor: palette.primary }]} />
                  <View style={[styles(themeColors, accentColor).paletteColor, { backgroundColor: palette.secondary }]} />
                  <View style={[styles(themeColors, accentColor).paletteColor, { backgroundColor: palette.accent }]} />
                </View>
                <Text style={styles(themeColors, accentColor).paletteName}>{palette.name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Primary Color */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Primary Color</Text>
          <View style={styles(themeColors, accentColor).colorInputContainer}>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={config?.theme?.primaryColor || '#34C759'}
              onChangeText={(value) => handleThemeChange('primaryColor', value)}
              placeholder="#34C759"
            />
            <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: config?.theme?.primaryColor || '#34C759' }]} />
          </View>
        </View>

        {/* Secondary Color */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Secondary Color</Text>
          <View style={styles(themeColors, accentColor).colorInputContainer}>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={config?.theme?.secondaryColor || '#F5F5F5'}
              onChangeText={(value) => handleThemeChange('secondaryColor', value)}
              placeholder="#F5F5F5"
            />
            <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: config?.theme?.secondaryColor || '#F5F5F5' }]} />
          </View>
        </View>

        {/* Accent Color */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Accent Color</Text>
          <View style={styles(themeColors, accentColor).colorInputContainer}>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={config?.theme?.accentColor || '#FF9500'}
              onChangeText={(value) => handleThemeChange('accentColor', value)}
              placeholder="#FF9500"
            />
            <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: config?.theme?.accentColor || '#FF9500' }]} />
          </View>
        </View>

        {/* Background Color */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Background Color</Text>
          <View style={styles(themeColors, accentColor).colorInputContainer}>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={config?.theme?.backgroundColor || '#FFFFFF'}
              onChangeText={(value) => handleThemeChange('backgroundColor', value)}
              placeholder="#FFFFFF"
            />
            <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: config?.theme?.backgroundColor || '#FFFFFF' }]} />
          </View>
        </View>

        {/* Text Color */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Text Color</Text>
          <View style={styles(themeColors, accentColor).colorInputContainer}>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={config?.theme?.textColor || '#1D1D1F'}
              onChangeText={(value) => handleThemeChange('textColor', value)}
              placeholder="#1D1D1F"
            />
            <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: config?.theme?.textColor || '#1D1D1F' }]} />
          </View>
        </View>

        {/* Font Family */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Font Family</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 8 }}>
            {fontFamilies.map((font, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles(themeColors, accentColor).optionButton,
                  config?.theme?.fontFamily === font && styles(themeColors, accentColor).optionButtonActive
                ]}
                onPress={() => handleThemeChange('fontFamily', font)}
              >
                <Text style={[
                  styles(themeColors, accentColor).optionButtonText,
                  config?.theme?.fontFamily === font && styles(themeColors, accentColor).optionButtonTextActive
                ]}>{font.split(',')[0]}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Layout Mode */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Layout Mode</Text>
          <View style={styles(themeColors, accentColor).optionRow}>
            {layoutModes.map((mode) => (
              <TouchableOpacity
                key={mode}
                style={[
                  styles(themeColors, accentColor).optionButton,
                  config?.theme?.layoutMode === mode && styles(themeColors, accentColor).optionButtonActive
                ]}
                onPress={() => handleThemeChange('layoutMode', mode)}
              >
                <Text style={[
                  styles(themeColors, accentColor).optionButtonText,
                  config?.theme?.layoutMode === mode && styles(themeColors, accentColor).optionButtonTextActive
                ]}>{mode}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Button Style */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Button Style</Text>
          <View style={styles(themeColors, accentColor).optionRow}>
            {buttonStyles.map((btnStyle) => (
              <TouchableOpacity
                key={btnStyle}
                style={[
                  styles(themeColors, accentColor).optionButton,
                  config?.theme?.buttonStyle === btnStyle && styles(themeColors, accentColor).optionButtonActive
                ]}
                onPress={() => handleThemeChange('buttonStyle', btnStyle)}
              >
                <Text style={[
                  styles(themeColors, accentColor).optionButtonText,
                  config?.theme?.buttonStyle === btnStyle && styles(themeColors, accentColor).optionButtonTextActive
                ]}>{btnStyle}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Border Radius */}
        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Border Radius</Text>
          <View style={styles(themeColors, accentColor).optionRow}>
            {borderRadiusOptions.map((radius) => (
              <TouchableOpacity
                key={radius}
                style={[
                  styles(themeColors, accentColor).optionButton,
                  config?.theme?.borderRadius === radius && styles(themeColors, accentColor).optionButtonActive
                ]}
                onPress={() => handleThemeChange('borderRadius', radius)}
              >
                <Text style={[
                  styles(themeColors, accentColor).optionButtonText,
                  config?.theme?.borderRadius === radius && styles(themeColors, accentColor).optionButtonTextActive
                ]}>{radius}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Typography Section */}
        <Text style={[styles(themeColors, accentColor).sectionTitle, { marginTop: 24 }]}>📝 Typography</Text>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>H1 Size</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.typography?.h1Size || '32px'}
            onChangeText={(value) => handleTypographyChange('h1Size', value)}
            placeholder="32px"
          />
        </View>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>H2 Size</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.typography?.h2Size || '24px'}
            onChangeText={(value) => handleTypographyChange('h2Size', value)}
            placeholder="24px"
          />
        </View>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Body Size</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.typography?.bodySize || '16px'}
            onChangeText={(value) => handleTypographyChange('bodySize', value)}
            placeholder="16px"
          />
        </View>

        {/* Spacing Section */}
        <Text style={[styles(themeColors, accentColor).sectionTitle, { marginTop: 24 }]}>📏 Spacing</Text>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Small Spacing</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.spacing?.small || '8px'}
            onChangeText={(value) => handleSpacingChange('small', value)}
            placeholder="8px"
          />
        </View>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Medium Spacing</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.spacing?.medium || '16px'}
            onChangeText={(value) => handleSpacingChange('medium', value)}
            placeholder="16px"
          />
        </View>

        <View style={styles(themeColors, accentColor).field}>
          <Text style={styles(themeColors, accentColor).label}>Large Spacing</Text>
          <TextInput
            style={styles(themeColors, accentColor).input}
            value={config?.theme?.spacing?.large || '24px'}
            onChangeText={(value) => handleSpacingChange('large', value)}
            placeholder="24px"
          />
        </View>

        {/* Block-Specific Styles */}
        {selectedBlock && (
          <>
            <Text style={[styles(themeColors, accentColor).sectionTitle, { marginTop: 24 }]}>🎯 Block Styles</Text>

            {selectedBlock.type === 'HEADER' && (
              <>
                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Background Color</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.bgColor || '#FFFFFF'}
                      onChangeText={(value) => handleConfigChange('bgColor', value)}
                      placeholder="#FFFFFF"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.bgColor || '#FFFFFF' }]} />
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Text Color</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.textColor || '#1D1D1F'}
                      onChangeText={(value) => handleConfigChange('textColor', value)}
                      placeholder="#1D1D1F"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.textColor || '#1D1D1F' }]} />
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Header Height</Text>
                  <View style={styles(themeColors, accentColor).optionRow}>
                    {['60px', '80px', '100px', '120px'].map((height) => (
                      <TouchableOpacity
                        key={height}
                        style={[
                          styles(themeColors, accentColor).optionButton,
                          selectedBlock.config.height === height && styles(themeColors, accentColor).optionButtonActive
                        ]}
                        onPress={() => handleConfigChange('height', height)}
                      >
                        <Text style={[
                          styles(themeColors, accentColor).optionButtonText,
                          selectedBlock.config.height === height && styles(themeColors, accentColor).optionButtonTextActive
                        ]}>{height}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Logo Size</Text>
                  <View style={styles(themeColors, accentColor).optionRow}>
                    {['40px', '50px', '60px', '80px'].map((size) => (
                      <TouchableOpacity
                        key={size}
                        style={[
                          styles(themeColors, accentColor).optionButton,
                          selectedBlock.config.logoHeight === size && styles(themeColors, accentColor).optionButtonActive
                        ]}
                        onPress={() => handleConfigChange('logoHeight', size)}
                      >
                        <Text style={[
                          styles(themeColors, accentColor).optionButtonText,
                          selectedBlock.config.logoHeight === size && styles(themeColors, accentColor).optionButtonTextActive
                        ]}>{size}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Sticky Header</Text>
                  <TouchableOpacity
                    style={styles(themeColors, accentColor).switchContainer}
                    onPress={() => handleConfigChange('sticky', !selectedBlock.config.sticky)}
                  >
                    <View
                      style={[
                        styles(themeColors, accentColor).switch,
                        selectedBlock.config.sticky && styles(themeColors, accentColor).switchActive
                      ]}
                    >
                      <View
                        style={[
                          styles(themeColors, accentColor).switchThumb,
                          selectedBlock.config.sticky && styles(themeColors, accentColor).switchThumbActive
                        ]}
                      />
                    </View>
                    <Text style={styles(themeColors, accentColor).switchLabel}>
                      {selectedBlock.config.sticky ? 'Enabled' : 'Disabled'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {selectedBlock.type === 'ANNOUNCEMENT_BAR' && (
              <>
                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Background Color</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.bgColor || '#34C759'}
                      onChangeText={(value) => {
                        console.log('🎨 [RightPanel] Announcement Bar bgColor changing to:', value);
                        handleConfigChange('bgColor', value);
                      }}
                      placeholder="#34C759"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.bgColor || '#34C759' }]} />
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Text Color</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.textColor || '#FFFFFF'}
                      onChangeText={(value) => {
                        console.log('🎨 [RightPanel] Announcement Bar textColor changing to:', value);
                        handleConfigChange('textColor', value);
                      }}
                      placeholder="#FFFFFF"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.textColor || '#FFFFFF' }]} />
                  </View>
                </View>
              </>
            )}

            {selectedBlock.type === 'HERO_BANNER' && (
              <>
                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Background Image URL</Text>
                  <TextInput
                    style={styles(themeColors, accentColor).input}
                    value={selectedBlock.config.bgImage || ''}
                    onChangeText={(value) => handleConfigChange('bgImage', value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Overlay Color</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.overlayColor || 'rgba(0,0,0,0.3)'}
                      onChangeText={(value) => handleConfigChange('overlayColor', value)}
                      placeholder="rgba(0,0,0,0.3)"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.overlayColor || 'rgba(0,0,0,0.3)' }]} />
                  </View>
                </View>
              </>
            )}

            {selectedBlock.type === 'PRODUCT_GRID' && (
              <>
                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Columns</Text>
                  <View style={styles(themeColors, accentColor).optionRow}>
                    {[2, 3, 4, 6].map((cols) => (
                      <TouchableOpacity
                        key={cols}
                        style={[
                          styles(themeColors, accentColor).optionButton,
                          selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonActive
                        ]}
                        onPress={() => handleConfigChange('columns', cols)}
                      >
                        <Text style={[
                          styles(themeColors, accentColor).optionButtonText,
                          selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonTextActive
                        ]}>{cols}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Card Background</Text>
                  <View style={styles(themeColors, accentColor).colorInputContainer}>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={selectedBlock.config.cardBgColor || '#FFFFFF'}
                      onChangeText={(value) => handleConfigChange('cardBgColor', value)}
                      placeholder="#FFFFFF"
                    />
                    <View style={[styles(themeColors, accentColor).colorPreview, { backgroundColor: selectedBlock.config.cardBgColor || '#FFFFFF' }]} />
                  </View>
                </View>
              </>
            )}

            {selectedBlock.type === 'IMAGE_GALLERY' && (
              <>
                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Grid Columns</Text>
                  <View style={styles(themeColors, accentColor).optionRow}>
                    {[2, 3, 4, 5].map((cols) => (
                      <TouchableOpacity
                        key={cols}
                        style={[
                          styles(themeColors, accentColor).optionButton,
                          selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonActive
                        ]}
                        onPress={() => handleConfigChange('columns', cols)}
                      >
                        <Text style={[
                          styles(themeColors, accentColor).optionButtonText,
                          selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonTextActive
                        ]}>{cols}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Grid Gap (px)</Text>
                  <View style={styles(themeColors, accentColor).optionRow}>
                    {[8, 12, 16, 24, 32].map((gap) => (
                      <TouchableOpacity
                        key={gap}
                        style={[
                          styles(themeColors, accentColor).optionButton,
                          selectedBlock.config.gap === gap && styles(themeColors, accentColor).optionButtonActive
                        ]}
                        onPress={() => handleConfigChange('gap', gap)}
                      >
                        <Text style={[
                          styles(themeColors, accentColor).optionButtonText,
                          selectedBlock.config.gap === gap && styles(themeColors, accentColor).optionButtonTextActive
                        ]}>{gap}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Enable Lightbox</Text>
                  <TouchableOpacity
                    style={styles(themeColors, accentColor).switchContainer}
                    onPress={() => handleConfigChange('lightbox', !selectedBlock.config.lightbox)}
                  >
                    <View
                      style={[
                        styles(themeColors, accentColor).switch,
                        selectedBlock.config.lightbox && styles(themeColors, accentColor).switchActive
                      ]}
                    >
                      <View
                        style={[
                          styles(themeColors, accentColor).switchThumb,
                          selectedBlock.config.lightbox && styles(themeColors, accentColor).switchThumbActive
                        ]}
                      />
                    </View>
                    <Text style={styles(themeColors, accentColor).switchLabel}>
                      {selectedBlock.config.lightbox ? 'Enabled' : 'Disabled'}
                    </Text>
                  </TouchableOpacity>
                  <Text style={styles(themeColors, accentColor).helpText}>
                    Allow images to be clicked and viewed in fullscreen
                  </Text>
                </View>

                <View style={styles(themeColors, accentColor).field}>
                  <Text style={styles(themeColors, accentColor).label}>Show Captions</Text>
                  <TouchableOpacity
                    style={styles(themeColors, accentColor).switchContainer}
                    onPress={() => handleConfigChange('showCaptions', !selectedBlock.config.showCaptions)}
                  >
                    <View
                      style={[
                        styles(themeColors, accentColor).switch,
                        selectedBlock.config.showCaptions && styles(themeColors, accentColor).switchActive
                      ]}
                    >
                      <View
                        style={[
                          styles(themeColors, accentColor).switchThumb,
                          selectedBlock.config.showCaptions && styles(themeColors, accentColor).switchThumbActive
                        ]}
                      />
                    </View>
                    <Text style={styles(themeColors, accentColor).switchLabel}>
                      {selectedBlock.config.showCaptions ? 'Visible' : 'Hidden'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </>
        )}
      </View>
    );
  };

  const renderContent = () => {
    return (
      <View style={styles(themeColors, accentColor).section}>
        <Text style={styles(themeColors, accentColor).sectionTitle}>Content</Text>

        {selectedBlock.type === 'HEADER' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Shop Logo</Text>

              {/* Hidden file input for logo */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />

              {/* Logo preview */}
              {imagePreviewUrl && (
                <View style={styles(themeColors, accentColor).imagePreview}>
                  <img
                    src={imagePreviewUrl}
                    alt="Logo preview"
                    style={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 8,
                      marginBottom: 8
                    }}
                  />
                </View>
              )}

              {/* Browse button */}
              <TouchableOpacity
                style={[
                  styles(themeColors, accentColor).browseButton,
                  uploadingImage && styles(themeColors, accentColor).browseButtonDisabled
                ]}
                onPress={handleBrowseImage}
                disabled={uploadingImage}
              >
                <Text style={styles(themeColors, accentColor).browseButtonText}>
                  {uploadingImage ? `⏳ Uploading ${uploadProgress}%...` : '📁 Browse Logo'}
                </Text>
              </TouchableOpacity>

              {/* Upload progress */}
              {uploadingImage && (
                <View style={styles(themeColors, accentColor).progressBar}>
                  <View
                    style={[
                      styles(themeColors, accentColor).progressFill,
                      { width: `${uploadProgress}%` }
                    ]}
                  />
                </View>
              )}

              <Text style={styles(themeColors, accentColor).helpText}>
                Square logo recommended (max 5MB)
              </Text>
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Shop Name</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.shopName || ''}
                onChangeText={(value) => handleConfigChange('shopName', value)}
                placeholder="Enter shop name..."
              />
            </View>
          </>
        )}

        {selectedBlock.type === 'ANNOUNCEMENT_BAR' && (
          <View style={styles(themeColors, accentColor).field}>
            <Text style={styles(themeColors, accentColor).label}>Announcement Text</Text>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={selectedBlock.config.text || ''}
              onChangeText={(value) => handleConfigChange('text', value)}
              placeholder="Enter announcement text..."
              multiline
            />
          </View>
        )}

        {selectedBlock.type === 'HERO_BANNER' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Background Image</Text>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageSelect}
              />

              {/* Image preview */}
              {imagePreviewUrl && (
                <View style={styles(themeColors, accentColor).imagePreview}>
                  <img
                    src={imagePreviewUrl}
                    alt="Background preview"
                    style={{
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 8,
                      marginBottom: 8
                    }}
                  />
                </View>
              )}

              {/* Browse button */}
              <TouchableOpacity
                style={[
                  styles(themeColors, accentColor).browseButton,
                  uploadingImage && styles(themeColors, accentColor).browseButtonDisabled
                ]}
                onPress={handleBrowseImage}
                disabled={uploadingImage}
              >
                <Text style={styles(themeColors, accentColor).browseButtonText}>
                  {uploadingImage ? `⏳ Uploading ${uploadProgress}%...` : '📁 Browse Image'}
                </Text>
              </TouchableOpacity>

              {/* Upload progress */}
              {uploadingImage && (
                <View style={styles(themeColors, accentColor).progressBar}>
                  <View
                    style={[
                      styles(themeColors, accentColor).progressFill,
                      { width: `${uploadProgress}%` }
                    ]}
                  />
                </View>
              )}

              <Text style={styles(themeColors, accentColor).helpText}>
                JPG, PNG, or WebP (max 5MB)
              </Text>
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="Enter title..."
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Subtitle</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.subtitle || ''}
                onChangeText={(value) => handleConfigChange('subtitle', value)}
                placeholder="Enter subtitle..."
                multiline
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Button Text</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.ctaText || ''}
                onChangeText={(value) => handleConfigChange('ctaText', value)}
                placeholder="Shop Now"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Display Location Section</Text>
              <TouchableOpacity
                style={styles(themeColors, accentColor).switchContainer}
                onPress={() => handleConfigChange('displayLocation', !(selectedBlock.config.displayLocation ?? false))}
              >
                <View
                  style={[
                    styles(themeColors, accentColor).switch,
                    (selectedBlock.config.displayLocation ?? false) && styles(themeColors, accentColor).switchActive
                  ]}
                >
                  <View
                    style={[
                      styles(themeColors, accentColor).switchThumb,
                      (selectedBlock.config.displayLocation ?? false) && styles(themeColors, accentColor).switchThumbActive
                    ]}
                  />
                </View>
                <Text style={styles(themeColors, accentColor).switchLabel}>
                  {(selectedBlock.config.displayLocation ?? false) ? 'Visible' : 'Hidden'}
                </Text>
              </TouchableOpacity>
              <Text style={styles(themeColors, accentColor).helpText}>
                Show location icon and address on hero banner
              </Text>
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Display Connect Section</Text>
              <TouchableOpacity
                style={styles(themeColors, accentColor).switchContainer}
                onPress={() => handleConfigChange('displayConnect', !(selectedBlock.config.displayConnect ?? false))}
              >
                <View
                  style={[
                    styles(themeColors, accentColor).switch,
                    (selectedBlock.config.displayConnect ?? false) && styles(themeColors, accentColor).switchActive
                  ]}
                >
                  <View
                    style={[
                      styles(themeColors, accentColor).switchThumb,
                      (selectedBlock.config.displayConnect ?? false) && styles(themeColors, accentColor).switchThumbActive
                    ]}
                  />
                </View>
                <Text style={styles(themeColors, accentColor).switchLabel}>
                  {(selectedBlock.config.displayConnect ?? false) ? 'Visible' : 'Hidden'}
                </Text>
              </TouchableOpacity>
              <Text style={styles(themeColors, accentColor).helpText}>
                Show contact/connect buttons on hero banner
              </Text>
            </View>
          </>
        )}

        {selectedBlock.type === 'PRODUCT_GRID' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Section Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="Our Products"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Show Search Bar</Text>
              <TouchableOpacity
                style={styles(themeColors, accentColor).switchContainer}
                onPress={() => handleConfigChange('showSearch', !selectedBlock.config.showSearch)}
              >
                <View
                  style={[
                    styles(themeColors, accentColor).switch,
                    selectedBlock.config.showSearch && styles(themeColors, accentColor).switchActive
                  ]}
                >
                  <View
                    style={[
                      styles(themeColors, accentColor).switchThumb,
                      selectedBlock.config.showSearch && styles(themeColors, accentColor).switchThumbActive
                    ]}
                  />
                </View>
                <Text style={styles(themeColors, accentColor).switchLabel}>
                  {selectedBlock.config.showSearch ? 'Visible' : 'Hidden'}
                </Text>
              </TouchableOpacity>
            </View>

            {selectedBlock.config.showSearch && (
              <View style={styles(themeColors, accentColor).field}>
                <Text style={styles(themeColors, accentColor).label}>Search Placeholder</Text>
                <TextInput
                  style={styles(themeColors, accentColor).input}
                  value={selectedBlock.config.searchPlaceholder || ''}
                  onChangeText={(value) => handleConfigChange('searchPlaceholder', value)}
                  placeholder="Search products..."
                />
              </View>
            )}
          </>
        )}

        {selectedBlock.type === 'TEXT_SECTION' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Heading</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.heading || ''}
                onChangeText={(value) => handleConfigChange('heading', value)}
                placeholder="Enter heading..."
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Content</Text>
              <TextInput
                style={[styles(themeColors, accentColor).input, { height: 120 }]}
                value={selectedBlock.config.content || ''}
                onChangeText={(value) => handleConfigChange('content', value)}
                placeholder="Enter content..."
                multiline
                numberOfLines={5}
              />
            </View>
          </>
        )}

        {selectedBlock.type === 'CONTACT_FORM' && (
          <View style={styles(themeColors, accentColor).field}>
            <Text style={styles(themeColors, accentColor).label}>Form Title</Text>
            <TextInput
              style={styles(themeColors, accentColor).input}
              value={selectedBlock.config.title || ''}
              onChangeText={(value) => handleConfigChange('title', value)}
              placeholder="Get In Touch"
            />
          </View>
        )}

        {selectedBlock.type === 'TESTIMONIALS' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Section Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="What Our Customers Say"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles(themeColors, accentColor).label}>Testimonials</Text>
                <TouchableOpacity
                  style={styles(themeColors, accentColor).addButton}
                  onPress={() => {
                    const newItems = [
                      ...(selectedBlock.config.items || []),
                      {
                        id: `testimonial-${Date.now()}`,
                        name: 'Customer Name',
                        role: 'Customer',
                        text: 'Great service and quality products!',
                        rating: 5,
                        avatar: ''
                      }
                    ];
                    handleConfigChange('items', newItems);
                  }}
                >
                  <Text style={styles(themeColors, accentColor).addButtonText}>+ Add Testimonial</Text>
                </TouchableOpacity>
              </View>

              {(selectedBlock.config.items || []).map((item: any, index: number) => (
                <View key={item.id || index} style={styles(themeColors, accentColor).listItem}>
                  <View style={styles(themeColors, accentColor).listItemHeader}>
                    <Text style={styles(themeColors, accentColor).listItemTitle}>Testimonial {index + 1}</Text>
                    <TouchableOpacity
                      style={styles(themeColors, accentColor).removeButton}
                      onPress={() => {
                        const newItems = selectedBlock.config.items.filter((_: any, i: number) => i !== index);
                        handleConfigChange('items', newItems);
                      }}
                    >
                      <Text style={styles(themeColors, accentColor).removeButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Customer Name</Text>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={item.name || ''}
                      onChangeText={(value) => {
                        const newItems = [...selectedBlock.config.items];
                        newItems[index] = { ...item, name: value };
                        handleConfigChange('items', newItems);
                      }}
                      placeholder="Enter name..."
                    />
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Role/Title</Text>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={item.role || ''}
                      onChangeText={(value) => {
                        const newItems = [...selectedBlock.config.items];
                        newItems[index] = { ...item, role: value };
                        handleConfigChange('items', newItems);
                      }}
                      placeholder="Customer, Business Owner, etc."
                    />
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Testimonial Text</Text>
                    <TextInput
                      style={[styles(themeColors, accentColor).input, { height: 80 }]}
                      value={item.text || ''}
                      onChangeText={(value) => {
                        const newItems = [...selectedBlock.config.items];
                        newItems[index] = { ...item, text: value };
                        handleConfigChange('items', newItems);
                      }}
                      placeholder="Enter testimonial..."
                      multiline
                      numberOfLines={3}
                    />
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Rating (1-5)</Text>
                    <View style={styles(themeColors, accentColor).optionRow}>
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <TouchableOpacity
                          key={rating}
                          style={[
                            styles(themeColors, accentColor).optionButton,
                            item.rating === rating && styles(themeColors, accentColor).optionButtonActive
                          ]}
                          onPress={() => {
                            const newItems = [...selectedBlock.config.items];
                            newItems[index] = { ...item, rating };
                            handleConfigChange('items', newItems);
                          }}
                        >
                          <Text style={[
                            styles(themeColors, accentColor).optionButtonText,
                            item.rating === rating && styles(themeColors, accentColor).optionButtonTextActive
                          ]}>{rating}⭐</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                </View>
              ))}
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Auto Play Carousel</Text>
              <TouchableOpacity
                style={styles(themeColors, accentColor).switchContainer}
                onPress={() => handleConfigChange('autoPlay', !selectedBlock.config.autoPlay)}
              >
                <View
                  style={[
                    styles(themeColors, accentColor).switch,
                    selectedBlock.config.autoPlay && styles(themeColors, accentColor).switchActive
                  ]}
                >
                  <View
                    style={[
                      styles(themeColors, accentColor).switchThumb,
                      selectedBlock.config.autoPlay && styles(themeColors, accentColor).switchThumbActive
                    ]}
                  />
                </View>
                <Text style={styles(themeColors, accentColor).switchLabel}>
                  {selectedBlock.config.autoPlay ? 'Enabled' : 'Disabled'}
                </Text>
              </TouchableOpacity>
            </View>

            {selectedBlock.config.autoPlay && (
              <View style={styles(themeColors, accentColor).field}>
                <Text style={styles(themeColors, accentColor).label}>Interval (ms)</Text>
                <TextInput
                  style={styles(themeColors, accentColor).input}
                  value={String(selectedBlock.config.interval || 5000)}
                  onChangeText={(value) => handleConfigChange('interval', parseInt(value) || 5000)}
                  placeholder="5000"
                  keyboardType="numeric"
                />
                <Text style={styles(themeColors, accentColor).helpText}>
                  Time between slides in milliseconds (e.g., 5000 = 5 seconds)
                </Text>
              </View>
            )}
          </>
        )}

        {selectedBlock.type === 'FAQ' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Section Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="Frequently Asked Questions"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles(themeColors, accentColor).label}>FAQ Items</Text>
                <TouchableOpacity
                  style={styles(themeColors, accentColor).addButton}
                  onPress={() => {
                    const newItems = [
                      ...(selectedBlock.config.items || []),
                      {
                        id: `faq-${Date.now()}`,
                        question: 'New Question?',
                        answer: 'Answer goes here.',
                        expanded: false
                      }
                    ];
                    handleConfigChange('items', newItems);
                  }}
                >
                  <Text style={styles(themeColors, accentColor).addButtonText}>+ Add FAQ</Text>
                </TouchableOpacity>
              </View>

              {(selectedBlock.config.items || []).map((item: any, index: number) => (
                <View key={item.id || index} style={styles(themeColors, accentColor).listItem}>
                  <View style={styles(themeColors, accentColor).listItemHeader}>
                    <Text style={styles(themeColors, accentColor).listItemTitle}>FAQ {index + 1}</Text>
                    <TouchableOpacity
                      style={styles(themeColors, accentColor).removeButton}
                      onPress={() => {
                        const newItems = selectedBlock.config.items.filter((_: any, i: number) => i !== index);
                        handleConfigChange('items', newItems);
                      }}
                    >
                      <Text style={styles(themeColors, accentColor).removeButtonText}>🗑️</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Question</Text>
                    <TextInput
                      style={styles(themeColors, accentColor).input}
                      value={item.question || ''}
                      onChangeText={(value) => {
                        const newItems = [...selectedBlock.config.items];
                        newItems[index] = { ...item, question: value };
                        handleConfigChange('items', newItems);
                      }}
                      placeholder="Enter question..."
                    />
                  </View>

                  <View style={styles(themeColors, accentColor).field}>
                    <Text style={styles(themeColors, accentColor).labelSmall}>Answer</Text>
                    <TextInput
                      style={[styles(themeColors, accentColor).input, { height: 100 }]}
                      value={item.answer || ''}
                      onChangeText={(value) => {
                        const newItems = [...selectedBlock.config.items];
                        newItems[index] = { ...item, answer: value };
                        handleConfigChange('items', newItems);
                      }}
                      placeholder="Enter answer..."
                      multiline
                      numberOfLines={4}
                    />
                  </View>
                </View>
              ))}
            </View>
          </>
        )}

        {selectedBlock.type === 'IMAGE_GALLERY' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Gallery Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="Image Gallery"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Subtitle</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.subtitle || ''}
                onChangeText={(value) => handleConfigChange('subtitle', value)}
                placeholder="Browse our collection"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text style={styles(themeColors, accentColor).label}>
                  Gallery Images ({(selectedBlock.config.images || []).length}/20)
                </Text>
                {(selectedBlock.config.images || []).length < 20 && (
                  <TouchableOpacity
                    style={styles(themeColors, accentColor).addButton}
                    onPress={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
                      input.multiple = false;
                      input.onchange = async (e: any) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        // Validate file type
                        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
                        if (!validTypes.includes(file.type)) {
                          Alert.alert('Invalid file type', 'Please select a JPG, PNG, or WebP image');
                          return;
                        }

                        // Validate file size (5MB max)
                        if (file.size > 5 * 1024 * 1024) {
                          Alert.alert('File too large', 'Please select an image under 5MB');
                          return;
                        }

                        try {
                          console.log('📤 [Gallery] Starting image upload...', { fileName: file.name, size: file.size });

                          // Get shop ID from config
                          const config = useWebsiteBuilder.getState().config;
                          if (!config || !config.shopId) {
                            Alert.alert('Error', 'Shop ID not found');
                            return;
                          }

                          const shopId = config.shopId;

                          // Generate unique ID for the image
                          const imageId = `gallery-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                          // Get upload URL from backend
                          console.log('📡 [Gallery] Requesting upload URL...', { shopId, type: 'website', productId: imageId });
                          const uploadResponse = await storageService.getUploadUrl({
                            shopId,
                            type: 'website',
                            productId: imageId,
                            fileName: file.name
                          });

                          console.log('✅ [Gallery] Upload URL received:', {
                            blobPath: uploadResponse.blobPath,
                            hasUploadUrl: !!uploadResponse.uploadUrl
                          });

                          // Upload to Azure Blob
                          console.log('📤 [Gallery] Uploading to Azure Blob...');
                          await storageService.uploadFile(uploadResponse.uploadUrl, file);
                          console.log('✅ [Gallery] Image uploaded successfully');

                          // Add to gallery images with blob path
                          const newImages = [
                            ...(selectedBlock.config.images || []),
                            {
                              id: imageId,
                              blobPath: uploadResponse.blobPath,
                              tag: '',
                              caption: '',
                              alt: file.name.replace(/\.[^/.]+$/, '')
                            }
                          ];

                          handleConfigChange('images', newImages);
                          Alert.alert('Success', 'Image uploaded successfully');
                        } catch (error) {
                          console.error('❌ [Gallery] Upload failed:', error);
                          Alert.alert('Upload failed', error instanceof Error ? error.message : 'Failed to upload image');
                        }
                      };
                      input.click();
                    }}
                  >
                    <Text style={styles(themeColors, accentColor).addButtonText}>+ Upload Image</Text>
                  </TouchableOpacity>
                )}
              </View>

              <Text style={styles(themeColors, accentColor).helpText}>
                Upload up to 20 images (JPG, PNG, WebP, max 5MB each)
              </Text>

              {/* Gallery Image Grid */}
              {(selectedBlock.config.images || []).length > 0 && (
                <View style={{ marginTop: 16 }}>
                  {(selectedBlock.config.images || []).map((image: any, index: number) => (
                    <View key={image.id || index} style={styles(themeColors, accentColor).listItem}>
                      <View style={styles(themeColors, accentColor).listItemHeader}>
                        <Text style={styles(themeColors, accentColor).listItemTitle}>Image {index + 1}</Text>
                        <TouchableOpacity
                          style={styles(themeColors, accentColor).removeButton}
                          onPress={() => {
                            const newImages = selectedBlock.config.images.filter((_: any, i: number) => i !== index);
                            handleConfigChange('images', newImages);
                          }}
                        >
                          <Text style={styles(themeColors, accentColor).removeButtonText}>🗑️</Text>
                        </TouchableOpacity>
                      </View>

                      {/* Image Preview */}
                      {image.blobPath && (
                        <GalleryImagePreview blobPath={image.blobPath} alt={image.alt || `Image ${index + 1}`} />
                      )}

                      {/* Tag Input */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Image Tag</Text>
                        <TextInput
                          style={styles(themeColors, accentColor).input}
                          value={image.tag || ''}
                          onChangeText={(value) => {
                            const newImages = [...selectedBlock.config.images];
                            newImages[index] = { ...image, tag: value };
                            handleConfigChange('images', newImages);
                          }}
                          placeholder="e.g., Product, Lifestyle, Feature..."
                          maxLength={50}
                        />
                      </View>

                      {/* Caption Input */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Caption (Optional)</Text>
                        <TextInput
                          style={styles(themeColors, accentColor).input}
                          value={image.caption || ''}
                          onChangeText={(value) => {
                            const newImages = [...selectedBlock.config.images];
                            newImages[index] = { ...image, caption: value };
                            handleConfigChange('images', newImages);
                          }}
                          placeholder="Image caption..."
                          maxLength={100}
                        />
                      </View>

                      {/* Alt Text Input */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Alt Text</Text>
                        <TextInput
                          style={styles(themeColors, accentColor).input}
                          value={image.alt || ''}
                          onChangeText={(value) => {
                            const newImages = [...selectedBlock.config.images];
                            newImages[index] = { ...image, alt: value };
                            handleConfigChange('images', newImages);
                          }}
                          placeholder="Describe the image..."
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {(selectedBlock.config.images || []).length === 0 && (
                <View style={{
                  padding: 24,
                  alignItems: 'center',
                  borderWidth: 2,
                  borderColor: '#E5E5E7',
                  borderStyle: 'dashed',
                  borderRadius: 8,
                  marginTop: 12
                }}>
                  <Text style={{ fontSize: 40, marginBottom: 8 }}>🖼️</Text>
                  <Text style={{ color: '#86868B', fontSize: 14, textAlign: 'center' }}>
                    No images yet. Upload your first image to start building your gallery.
                  </Text>
                </View>
              )}
            </View>
          </>
        )}

        {selectedBlock.type === 'CUSTOM_HTML' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Custom HTML Content</Text>
              <Text style={styles(themeColors, accentColor).helpText}>
                Paste HTML generated from AI tools or custom code. Script tags and inline JavaScript will be removed for security.
              </Text>

              {/* HTML Editor */}
              <TextInput
                style={[styles(themeColors, accentColor).input, {
                  height: 300,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  padding: 12
                }]}
                value={customHtml}
                onChangeText={(value) => {
                  setCustomHtml(value);
                  setHtmlErrors([]);
                  setHtmlWarnings([]);
                }}
                placeholder="<div>Your custom HTML here...</div>"
                multiline
                numberOfLines={15}
                editable={true}
                autoCorrect={false}
                autoCapitalize="none"
                spellCheck={false}
              />

              <Text style={styles(themeColors, accentColor).helpText}>
                {customHtml.length}/204800 characters
              </Text>

              {/* Validation Errors */}
              {htmlErrors.length > 0 && (
                <View style={{
                  backgroundColor: '#FFF3F3',
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 12,
                  borderLeftWidth: 3,
                  borderLeftColor: '#FF3B30'
                }}>
                  <Text style={{ color: '#FF3B30', fontWeight: '600', marginBottom: 8 }}>
                    ❌ Validation Errors:
                  </Text>
                  {htmlErrors.map((error, index) => (
                    <Text key={index} style={{ color: '#FF3B30', fontSize: 13, marginBottom: 4 }}>
                      • {error}
                    </Text>
                  ))}
                </View>
              )}

              {/* Validation Warnings */}
              {htmlWarnings.length > 0 && (
                <View style={{
                  backgroundColor: '#FFF9E6',
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 12,
                  borderLeftWidth: 3,
                  borderLeftColor: '#FF9500'
                }}>
                  <Text style={{ color: '#FF9500', fontWeight: '600', marginBottom: 8 }}>
                    ⚠️ Warnings:
                  </Text>
                  {htmlWarnings.map((warning, index) => (
                    <Text key={index} style={{ color: '#FF9500', fontSize: 13, marginBottom: 4 }}>
                      • {warning}
                    </Text>
                  ))}
                </View>
              )}

              {/* Validate & Apply Button */}
              <TouchableOpacity
                style={[
                  styles(themeColors, accentColor).validateButton,
                  isValidatingHtml && { opacity: 0.6 }
                ]}
                onPress={async () => {
                  setIsValidatingHtml(true);
                  try {
                    console.log('🔍 [CustomHTML] Validating HTML...');

                    const result = validateAndSanitizeHtml(customHtml || '');

                    if (!result.isValid) {
                      setHtmlErrors(result.errors);
                      setHtmlWarnings(result.warnings);
                      Alert.alert('Validation Failed', result.errors.join('\n'));
                    } else {
                      setHtmlErrors([]);
                      setHtmlWarnings(result.warnings);

                      // Save sanitized HTML - use single updateBlock call to avoid stale closure issues
                      // (multiple sequential handleConfigChange calls each spread ...selectedBlock.config
                      // from the current render snapshot, so later calls overwrite earlier ones)
                      if (selectedBlock) {
                        console.log('✅ [CustomHTML] Applying sanitized HTML to block atomically');
                        updateBlock(selectedBlock.id, {
                          config: {
                            ...selectedBlock.config,
                            html: result.sanitizedHtml,
                            version: (selectedBlock.config.version || 0) + 1,
                            source: 'manual',
                            lastValidated: new Date().toISOString(),
                          }
                        });
                        // Also sync local textarea state to sanitized HTML
                        setCustomHtml(result.sanitizedHtml);
                      }

                      if (result.warnings.length > 0) {
                        Alert.alert(
                          'HTML Applied with Warnings',
                          `HTML has been sanitized and applied.\n\nWarnings:\n${result.warnings.join('\n')}`
                        );
                      } else {
                        Alert.alert('Success', 'HTML validated and applied successfully!');
                      }
                    }
                  } catch (error) {
                    console.error('❌ [CustomHTML] Validation error:', error);
                    Alert.alert('Validation Error', error instanceof Error ? error.message : 'Failed to validate HTML');
                  } finally {
                    setIsValidatingHtml(false);
                  }
                }}
                disabled={isValidatingHtml || !customHtml}
              >
                <Text style={styles(themeColors, accentColor).validateButtonText}>
                  {isValidatingHtml ? '⏳ Validating...' : '✅ Validate & Apply'}
                </Text>
              </TouchableOpacity>

              {/* Preview Toggle */}
              <TouchableOpacity
                style={[
                  styles(themeColors, accentColor).previewToggleButton,
                  { marginTop: 12 }
                ]}
                onPress={() => setShowHtmlPreview(!showHtmlPreview)}
              >
                <Text style={styles(themeColors, accentColor).previewToggleButtonText}>
                  {showHtmlPreview ? '👁️ Hide Preview' : '👁️ Show Preview'}
                </Text>
              </TouchableOpacity>

              {/* Preview */}
              {showHtmlPreview && selectedBlock.config.html && (
                <View style={{
                  marginTop: 12,
                  borderWidth: 1,
                  borderColor: '#E5E5E7',
                  borderRadius: 8,
                  overflow: 'hidden'
                }}>
                  <View style={{
                    backgroundColor: '#F5F5F7',
                    padding: 8,
                    borderBottomWidth: 1,
                    borderBottomColor: '#E5E5E7'
                  }}>
                    <Text style={{ fontSize: 12, color: '#86868B', fontWeight: '600' }}>
                      🔒 Preview (Sanitized HTML)
                    </Text>
                  </View>
                  {/*
                    Using dangerouslySetInnerHTML instead of sandboxed iframe.
                    HTML has been sanitized by DOMPurify to remove scripts and unsafe content.
                  */}
                  <div
                    style={{
                      padding: 16,
                      backgroundColor: '#FFFFFF',
                      minHeight: 100,
                      maxHeight: 400,
                      overflow: 'auto',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      boxSizing: 'border-box'
                    }}
                    dangerouslySetInnerHTML={{ __html: selectedBlock.config.html }}
                  />
                </View>
              )}

              {/* Security Notice */}
              <View style={{
                marginTop: 12,
                padding: 12,
                backgroundColor: '#E3F2FD',
                borderRadius: 8,
                borderLeftWidth: 3,
                borderLeftColor: '#007AFF'
              }}>
                <Text style={{ fontSize: 12, color: '#007AFF', fontWeight: '600', marginBottom: 4 }}>
                  🔒 Security Information
                </Text>
                <Text style={{ fontSize: 12, color: '#007AFF' }}>
                  • Script tags are automatically removed{'\n'}
                  • Inline JavaScript events are stripped{'\n'}
                  • External scripts are blocked{'\n'}
                  • HTML is sanitized before rendering
                </Text>
              </View>
            </View>
          </>
        )}

        {selectedBlock.type === 'SOCIAL_FEED' && (
          <>
            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Section Title</Text>
              <TextInput
                style={styles(themeColors, accentColor).input}
                value={selectedBlock.config.title || ''}
                onChangeText={(value) => handleConfigChange('title', value)}
                placeholder="Follow Us"
              />
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <Text style={styles(themeColors, accentColor).label}>Grid Columns</Text>
              <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                {[2, 3, 4].map(cols => (
                  <TouchableOpacity
                    key={cols}
                    style={[
                      styles(themeColors, accentColor).optionButton,
                      selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonActive
                    ]}
                    onPress={() => handleConfigChange('columns', cols)}
                  >
                    <Text style={[
                      styles(themeColors, accentColor).optionButtonText,
                      selectedBlock.config.columns === cols && styles(themeColors, accentColor).optionButtonTextActive
                    ]}>
                      {cols} Columns
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={styles(themeColors, accentColor).label}>Show Captions</Text>
                <TouchableOpacity
                  style={[
                    styles(themeColors, accentColor).toggle,
                    selectedBlock.config.showCaptions !== false && styles(themeColors, accentColor).toggleActive
                  ]}
                  onPress={() => handleConfigChange('showCaptions', !(selectedBlock.config.showCaptions !== false))}
                >
                  <View style={[
                    styles(themeColors, accentColor).toggleThumb,
                    selectedBlock.config.showCaptions !== false && styles(themeColors, accentColor).toggleThumbActive
                  ]} />
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles(themeColors, accentColor).field}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text style={styles(themeColors, accentColor).label}>
                  Social Feeds ({(selectedBlock.config.feeds || []).length})
                </Text>
                <TouchableOpacity
                  style={styles(themeColors, accentColor).addButton}
                  onPress={() => {
                    const newFeed = {
                      id: `feed-${Date.now()}`,
                      platform: 'Instagram',
                      link: '',
                      caption: '',
                      mediaType: 'image',
                      previewUrl: ''
                    };
                    const newFeeds = [...(selectedBlock.config.feeds || []), newFeed];
                    handleConfigChange('feeds', newFeeds);
                  }}
                >
                  <Text style={styles(themeColors, accentColor).addButtonText}>+ Add Feed</Text>
                </TouchableOpacity>
              </View>

              <Text style={styles(themeColors, accentColor).helpText}>
                Add your social media posts from Instagram, Twitter/X, Threads, YouTube, Facebook, LinkedIn, TikTok, Pinterest, or WhatsApp
              </Text>

              {/* Social Feeds List */}
              {(selectedBlock.config.feeds || []).length > 0 && (
                <View style={{ marginTop: 16 }}>
                  {(selectedBlock.config.feeds || []).map((feed: any, index: number) => (
                    <View key={feed.id || index} style={styles(themeColors, accentColor).listItem}>
                      <View style={styles(themeColors, accentColor).listItemHeader}>
                        <Text style={styles(themeColors, accentColor).listItemTitle}>
                          {feed.platform} Feed {index + 1}
                        </Text>
                        <TouchableOpacity
                          style={styles(themeColors, accentColor).removeButton}
                          onPress={() => {
                            const newFeeds = selectedBlock.config.feeds.filter((_: any, i: number) => i !== index);
                            handleConfigChange('feeds', newFeeds);
                          }}
                        >
                          <Text style={styles(themeColors, accentColor).removeButtonText}>🗑️</Text>
                        </TouchableOpacity>
                      </View>

                      {/* Platform Selector */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Platform</Text>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                          {['Instagram', 'Twitter', 'X', 'Threads', 'YouTube', 'Facebook', 'LinkedIn', 'TikTok', 'Pinterest', 'WhatsApp'].map(platform => (
                            <TouchableOpacity
                              key={platform}
                              style={[
                                styles(themeColors, accentColor).platformButton,
                                feed.platform === platform && {
                                  backgroundColor: getPlatformColor(platform),
                                  borderColor: getPlatformColor(platform)
                                }
                              ]}
                              onPress={() => {
                                const newFeeds = [...selectedBlock.config.feeds];
                                newFeeds[index] = { ...feed, platform };
                                handleConfigChange('feeds', newFeeds);
                              }}
                            >
                              <Text style={[
                                styles(themeColors, accentColor).platformButtonText,
                                feed.platform === platform && { color: '#FFFFFF' }
                              ]}>
                                {getPlatformIcon(platform)} {platform}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      {/* Media Type */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Media Type</Text>
                        <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                          {['image', 'video', 'story'].map(type => (
                            <TouchableOpacity
                              key={type}
                              style={[
                                styles(themeColors, accentColor).optionButton,
                                feed.mediaType === type && styles(themeColors, accentColor).optionButtonActive
                              ]}
                              onPress={() => {
                                const newFeeds = [...selectedBlock.config.feeds];
                                newFeeds[index] = { ...feed, mediaType: type };
                                handleConfigChange('feeds', newFeeds);
                              }}
                            >
                              <Text style={[
                                styles(themeColors, accentColor).optionButtonText,
                                feed.mediaType === type && styles(themeColors, accentColor).optionButtonTextActive
                              ]}>
                                {type === 'image' ? '📷' : type === 'video' ? '🎥' : '⚡'} {type.charAt(0).toUpperCase() + type.slice(1)}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      </View>

                      {/* Post/Account Link */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Post/Account Link</Text>
                        <TextInput
                          style={styles(themeColors, accentColor).input}
                          value={feed.link || ''}
                          onChangeText={(value) => {
                            const newFeeds = [...selectedBlock.config.feeds];
                            newFeeds[index] = { ...feed, link: value };
                            handleConfigChange('feeds', newFeeds);
                          }}
                          placeholder={`https://${feed.platform.toLowerCase()}.com/...`}
                        />
                      </View>

                      {/* Caption */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Caption</Text>
                        <TextInput
                          style={[styles(themeColors, accentColor).input, { height: 80 }]}
                          value={feed.caption || ''}
                          onChangeText={(value) => {
                            const newFeeds = [...selectedBlock.config.feeds];
                            newFeeds[index] = { ...feed, caption: value };
                            handleConfigChange('feeds', newFeeds);
                          }}
                          placeholder="Enter your post caption..."
                          multiline
                          numberOfLines={4}
                          maxLength={300}
                        />
                        <Text style={styles(themeColors, accentColor).helpText}>
                          {(feed.caption || '').length}/300
                        </Text>
                      </View>

                      {/* Preview URL (Optional) */}
                      <View style={styles(themeColors, accentColor).field}>
                        <Text style={styles(themeColors, accentColor).labelSmall}>Preview Image URL (Optional)</Text>
                        <TextInput
                          style={styles(themeColors, accentColor).input}
                          value={feed.previewUrl || ''}
                          onChangeText={(value) => {
                            const newFeeds = [...selectedBlock.config.feeds];
                            newFeeds[index] = { ...feed, previewUrl: value };
                            handleConfigChange('feeds', newFeeds);
                          }}
                          placeholder="https://..."
                        />
                        <Text style={styles(themeColors, accentColor).helpText}>
                          Optional: Provide a direct image URL for preview
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {(selectedBlock.config.feeds || []).length === 0 && (
                <View style={{
                  padding: 24,
                  alignItems: 'center',
                  borderWidth: 2,
                  borderColor: '#E5E5E7',
                  borderStyle: 'dashed',
                  borderRadius: 8,
                  marginTop: 12
                }}>
                  <Text style={{ fontSize: 40, marginBottom: 8 }}>📱</Text>
                  <Text style={{ color: '#86868B', fontSize: 14, textAlign: 'center' }}>
                    No social feeds yet. Click "+ Add Feed" to start adding your social media posts.
                  </Text>
                </View>
              )}
            </View>
          </>
        )}
      </View>
    );
  };


  return (
    <View style={styles(themeColors, accentColor).container}>
      {/* Tabs */}
      <View style={styles(themeColors, accentColor).tabs}>
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles(themeColors, accentColor).tab,
              activeTab === tab.id && styles(themeColors, accentColor).tabActive,
            ]}
            onPress={() => setActiveTab(tab.id as any)}
          >
            <Text style={[
              styles(themeColors, accentColor).tabText,
              activeTab === tab.id && styles(themeColors, accentColor).tabTextActive,
            ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Content */}
      <ScrollView style={styles(themeColors, accentColor).content}>
        {activeTab === 'settings' && renderSettings()}
        {activeTab === 'style' && renderStyle()}
        {activeTab === 'content' && renderContent()}
      </ScrollView>
    </View>
  );
};

const styles = (themeColors: any, accentColor: string) => StyleSheet.create({
  container: {
    width: 350,
    backgroundColor: themeColors.background || '#FFFFFF',
    borderLeftWidth: 1,
    borderLeftColor: themeColors.border || "#E5E5E5",
    flexDirection: 'column',
  },
  tabs: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: themeColors.border || "#E5E5E5",
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: accentColor,
  },
  tabText: {
    fontSize: 12,
    color: themeColors.text || "#000000",
  },
  tabTextActive: {
    color: accentColor,
    fontWeight: '600',
  },
  content: {
    flex: 1,
    padding: 16,
  },
  section: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: themeColors.text || "#000000",
    marginBottom: 8,
  },
  field: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: themeColors.text || "#000000",
  },
  value: {
    fontSize: 14,
    color: "#666666",
    padding: 12,
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderRadius: 6,
  },
  input: {
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: themeColors.text || "#000000",
    flex: 1,
  },
  hint: {
    fontSize: 12,
    color: "#666666",
    fontStyle: 'italic',
  },
  deleteButton: {
    backgroundColor: '#FF3B30',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  deleteButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: themeColors.text || "#000000",
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: "#666666",
    textAlign: 'center',
  },
  // New styles for color palettes
  paletteCard: {
    marginRight: 12,
    padding: 8,
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    minWidth: 100,
  },
  paletteColors: {
    flexDirection: 'row',
    gap: 4,
    marginBottom: 8,
  },
  paletteColor: {
    width: 28,
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  paletteName: {
    fontSize: 11,
    color: themeColors.text || "#000000",
    textAlign: 'center',
  },
  // Color input with preview
  colorInputContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  colorPreview: {
    width: 40,
    height: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  // Option buttons
  optionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  optionButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
  },
  optionButtonActive: {
    backgroundColor: accentColor,
    borderColor: accentColor,
  },
  optionButtonText: {
    fontSize: 12,
    color: themeColors.text || "#000000",
    textTransform: 'capitalize',
  },
  optionButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  // Image upload styles
  imagePreview: {
    width: '100%',
    marginBottom: 8,
  },
  browseButton: {
    backgroundColor: accentColor,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginVertical: 8,
  },
  browseButtonDisabled: {
    backgroundColor: '#CCCCCC',
    opacity: 0.6,
  },
  browseButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 14,
  },
  progressBar: {
    height: 4,
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderRadius: 2,
    overflow: 'hidden',
    marginVertical: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: accentColor,
  },
  helpText: {
    fontSize: 12,
    color: '#666666',
    marginTop: 4,
  },
  // Switch/Toggle styles
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  switch: {
    width: 50,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#E5E5E5',
    padding: 2,
    justifyContent: 'center',
  },
  switchActive: {
    backgroundColor: accentColor,
  },
  switchThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  switchThumbActive: {
    alignSelf: 'flex-end',
  },
  switchLabel: {
    fontSize: 14,
    color: themeColors.text || "#000000",
  },
  // List items (for testimonials and FAQ)
  listItem: {
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    gap: 12,
  },
  listItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  listItemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: themeColors.text || "#000000",
  },
  addButton: {
    backgroundColor: accentColor,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  removeButton: {
    backgroundColor: '#FF3B30',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  removeButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
  },
  labelSmall: {
    fontSize: 12,
    fontWeight: '600',
    color: themeColors.text || "#000000",
  },
  addButtonDisabled: {
    opacity: 0.5,
  },
  galleryGrid: {
    gap: 16,
    marginTop: 12,
  },
  galleryItem: {
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    borderRadius: 8,
    padding: 12,
    backgroundColor: themeColors.background || '#FFFFFF',
  },
  galleryImageContainer: {
    position: 'relative',
    width: '100%',
    height: 160,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: themeColors.secondary || '#F5F5F5',
    marginBottom: 8,
  },
  galleryPlaceholder: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: themeColors.secondary || '#F5F5F5',
  },
  galleryPlaceholderText: {
    fontSize: 32,
  },
  galleryRemoveButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(255, 59, 48, 0.9)',
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  galleryRemoveButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  galleryOrderBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 12,
  },
  galleryOrderText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  galleryItemDetails: {
    gap: 8,
  },
  inputSmall: {
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    fontSize: 13,
    backgroundColor: themeColors.background || '#FFFFFF',
    color: themeColors.text || "#000000",
  },
  galleryEmptyState: {
    padding: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: themeColors.secondary || '#F5F5F5',
    borderRadius: 8,
    marginTop: 12,
  },
  galleryEmptyIcon: {
    fontSize: 48,
    marginBottom: 8,
  },
  galleryEmptyText: {
    fontSize: 16,
    fontWeight: '600',
    color: themeColors.text || "#000000",
    marginBottom: 4,
  },
  galleryEmptySubtext: {
    fontSize: 13,
    color: themeColors.textSecondary || "#8E8E93",
    textAlign: 'center',
  },
  // Platform buttons for social feed
  platformButton: {
    borderWidth: 1,
    borderColor: themeColors.border || "#E5E5E5",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: themeColors.background || '#FFFFFF',
  },
  platformButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: themeColors.text || "#000000",
  },
  // Custom HTML buttons
  validateButton: {
    backgroundColor: '#34C759',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 12,
    alignItems: 'center',
  },
  validateButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  previewToggleButton: {
    backgroundColor: themeColors.secondary || '#F5F5F7',
    borderWidth: 1,
    borderColor: themeColors.border || '#E5E5E7',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  previewToggleButtonText: {
    color: accentColor,
    fontSize: 13,
    fontWeight: '600',
  },
});

